import type { Rs485SensorModel } from './generated/Rs485SensorModel.js';
import type { TagType } from './generated/TagType.js';

export interface Rs485FieldCatalogEntry {
  field: string;
  /** Human units for the derived tag (null = dimensionless/coded). */
  units: string | null;
  /** Human label for the field in pickers/lists. */
  label: string;
}

/**
 * Single source of truth for RS485 modbus sensor fields: which fields each
 * model emits (name + ORDER, pinned by tag-keys.test.ts — derived tag keys
 * must stay stable), plus their human units/label for pickers and the
 * derived-tag `units` backfill (tag-sync.ts).
 */
export const RS485_FIELD_CATALOG: Record<Rs485SensorModel, Rs485FieldCatalogEntry[]> = {
  mlx90614: [
    { field: 'ambient_c_x100', units: '°C×100', label: 'Ambient temperature' },
    { field: 'object_c_x100', units: '°C×100', label: 'Object temperature' },
    { field: 'age_ms', units: 'ms', label: 'Reading age' },
  ],
  mfrc522: [
    { field: 'uid_hex', units: null, label: 'Card UID (hex)' },
    { field: 'uid_len', units: 'bytes', label: 'UID length' },
    { field: 'scan_count', units: null, label: 'Scan count' },
    { field: 'present', units: null, label: 'Card present' },
  ],
  hx711: [
    { field: 'weight', units: 'g', label: 'Weight' },
    { field: 'units_code', units: null, label: 'Units code' },
    { field: 'age_ms', units: 'ms', label: 'Reading age' },
  ],
  adc_loop: [
    { field: 'raw', units: 'counts', label: 'Raw ADC' },
    { field: 'engineering_x100', units: 'eng×100', label: 'Engineering value' },
    { field: 'units_code', units: null, label: 'Units code' },
    { field: 'age_ms', units: 'ms', label: 'Reading age' },
  ],
  vl53: [
    { field: 'range_mm', units: 'mm', label: 'Distance' },
    { field: 'range_status', units: null, label: 'Range status' },
    { field: 'signal_rate_x256', units: 'Mcps×256', label: 'Signal rate' },
    { field: 'age_ms', units: 'ms', label: 'Reading age' },
  ],
};

/** Back-compat: the field-name-only view some callers still use. */
export const RS485_FIELDS_BY_MODEL: Record<Rs485SensorModel, string[]> = Object.fromEntries(
  Object.entries(RS485_FIELD_CATALOG).map(([m, entries]) => [m, entries.map((e) => e.field)]),
) as Record<Rs485SensorModel, string[]>;

/** The models, catalog-derived (replaces hand-duplicated MODEL_OPTIONS lists). */
export const RS485_SENSOR_MODELS = Object.keys(RS485_FIELD_CATALOG) as Rs485SensorModel[];

// Field → tag value domain. Booleans for presence flags, strings for hex ids,
// everything else integer-scaled on the wire.
const STRING_FIELDS = new Set(['uid_hex']);
const BOOL_FIELDS = new Set(['present']);
export function fieldTagType(field: string): TagType {
  if (BOOL_FIELDS.has(field)) return 'bool';
  if (STRING_FIELDS.has(field)) return 'string';
  return 'int';
}

export type DerivedSensorTag = {
  field: string;
  key: string;
  tag_type: TagType;
  units: string | null;
};

/**
 * One derived tag per emitted field of an RS485 modbus sensor. Wire key =
 * `rs485/<board.can_id>/<modbus_slave_id>/<field>` (fh-iot wiring.rs:3251).
 * Returns [] unless the sensor is modbus-addressed and the board can_id resolves.
 */
export function deriveSensorTagKeys(
  sensor: { modbus_slave_id: number | null; model: Rs485SensorModel | null },
  board: { can_id: number } | null,
): DerivedSensorTag[] {
  if (!board || sensor.modbus_slave_id == null || sensor.model == null) return [];
  const entries = RS485_FIELD_CATALOG[sensor.model] ?? [];
  return entries.map((e) => ({
    field: e.field,
    key: `rs485/${board.can_id}/${sensor.modbus_slave_id}/${e.field}`,
    tag_type: fieldTagType(e.field),
    units: e.units,
  }));
}
